import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Markdown } from "./Markdown";
import { CheckCircle, Robot, User, XCircle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export function AssistantChat({ compact = false, sid: sidProp, onSidChange, starter = "" }) {
  const [sidLocal, setSidLocal] = useState(() => localStorage.getItem("assistant_sid") || null);
  const controlled = sidProp !== undefined;
  const sid = controlled ? sidProp : sidLocal;
  const setSid = (v) => {
    if (controlled) onSidChange?.(v);
    else {
      setSidLocal(v);
      if (v) localStorage.setItem("assistant_sid", v);
      else localStorage.removeItem("assistant_sid");
    }
  };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (starter) setInput(starter); }, [starter]);
  const bottomRef = useRef(null);
  const streamingRef = useRef(false);

  useEffect(() => {
    if (streamingRef.current) return;
    if (!sid) { setMessages([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/assistant/sessions/${sid}/messages`);
        setMessages(data.messages || []);
      } catch { /* fresh session */ }
    })();
  }, [sid]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const patchLast = (fn) => setMessages((m) => {
    const c = [...m];
    c[c.length - 1] = fn({ ...c[c.length - 1] });
    return c;
  });

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    streamingRef.current = true;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);
    const ctrl = new AbortController();
    let lastEvent = Date.now();
    const watchdog = setInterval(() => { if (Date.now() - lastEvent > 60000) ctrl.abort(); }, 5000);
    try {
      const res = await fetch(`${API}/api/assistant/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sid }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`Assistant request failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      const apply = (evt) => {
        lastEvent = Date.now();
        if (evt.type === "session") setSid(evt.session_id);
        if (evt.type === "token") patchLast((msg) => ({ ...msg, content: msg.content + evt.text }));
        if (evt.type === "action") patchLast((msg) => ({ ...msg, action: evt.action }));
        if (evt.type === "error") {
          patchLast((msg) => ({ ...msg, streaming: false, content: msg.content || evt.text }));
          toast.error(evt.text);
        }
      };
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (line) { try { apply(JSON.parse(line)); } catch { /* partial */ } }
        }
      }
      patchLast((msg) => (msg.content || msg.action
        ? { ...msg, streaming: false }
        : { ...msg, streaming: false, content: "No response came through — please try sending again." }));
    } catch (e) {
      if (ctrl.signal.aborted) {
        patchLast((msg) => ({ ...msg, streaming: false, content: msg.content || "The assistant stopped responding (timed out) — please try again." }));
        toast.error("Assistant timed out — please try again");
      } else {
        toast.error(formatApiError(e));
        setMessages((m) => m.slice(0, -1));
      }
    } finally {
      clearInterval(watchdog);
      streamingRef.current = false;
      setBusy(false);
    }
  };

  const act = async (actionId, decision) => {
    try {
      await api.post(`/assistant/actions/${actionId}/${decision}`);
      setMessages((m) => m.map((msg) => msg.action?.id === actionId
        ? { ...msg, action: { ...msg.action, status: decision === "confirm" ? "confirmed" : "cancelled" } }
        : msg));
      toast.success(decision === "confirm" ? "Done — change applied" : "Cancelled, nothing changed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-testid={compact ? "assistant-chat-compact" : "assistant-chat"}>
      <div className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="assistant-messages">
        {messages.length === 0 && (
          <div className="mx-auto mt-10 max-w-md text-center" data-testid="assistant-empty">
            <Robot size={40} className="mx-auto text-stone-300" />
            <p className="mt-3 text-sm text-stone-500">Try: &ldquo;Who is overdue on Abuse training?&rdquo; · &ldquo;Aretha Hall is no longer an employee&rdquo; · &ldquo;Record a gap: no written policy for weekend on-call&rdquo;</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`assistant-msg-${i}`}>
            {m.role !== "user" && <Robot size={20} className="mt-1 shrink-0 text-stone-400" />}
            <div className={`${compact ? "max-w-[88%]" : "max-w-[75%]"} rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "border border-stone-200 bg-stone-50 text-stone-800"}`}>
              {m.role === "user" ? (
                <p className="whitespace-pre-wrap">{m.content}</p>
              ) : (
                <Markdown text={m.content + (m.streaming ? " ▌" : "")} />
              )}
              {m.action && (
                <div className="mt-3 rounded-md border border-primary/30 bg-white p-3" data-testid={`action-card-${m.action.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-accent-foreground">Proposed change — review before I do it</p>
                  <p className="mt-1 text-sm font-semibold text-stone-900">{m.action.title}</p>
                  <ul className="mt-1 list-disc pl-4 text-xs text-stone-600">
                    {(m.action.lines || []).map((l, j) => <li key={j}>{l}</li>)}
                  </ul>
                  {m.action.status === "pending" && (
                    <div className="mt-2.5 flex gap-2">
                      <Button size="sm" onClick={() => act(m.action.id, "confirm")} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`action-confirm-${m.action.id}`}><CheckCircle size={14} className="mr-1" /> Confirm</Button>
                      <Button size="sm" variant="outline" onClick={() => act(m.action.id, "cancel")} className="border-stone-300" data-testid={`action-cancel-${m.action.id}`}><XCircle size={14} className="mr-1" /> Cancel</Button>
                    </div>
                  )}
                  {m.action.status === "confirmed" && <p className="mt-2 text-xs font-semibold text-[#1e8449]" data-testid={`action-done-${m.action.id}`}>Confirmed — change applied</p>}
                  {m.action.status === "cancelled" && <p className="mt-2 text-xs font-semibold text-stone-400">Cancelled — nothing was changed</p>}
                </div>
              )}
            </div>
            {m.role === "user" && <User size={20} className="mt-1 shrink-0 text-stone-400" />}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-stone-200 p-3">
        <div className="flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask a question or tell me what to do…"
            className="h-10 flex-1 rounded-md border border-stone-300 bg-white px-3 text-sm focus:border-primary focus:outline-none"
            data-testid="assistant-input" />
          <Button onClick={send} disabled={busy || !input.trim()} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="assistant-send">
            {busy ? "Thinking…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}
